import { supabase } from './supabase';

/**
 * Enhanced API client that handles token refresh automatically
 * This replaces manual fetch calls with automatic session management
 */

class ApiClient {
  constructor(supabaseClient = supabase) {
    this.supabaseClient = supabaseClient;
    this.baseURL = import.meta.env.VITE_API_URL;

    if (!this.baseURL) {
      console.error('Missing VITE_API_URL');
    }
  }

  /**
   * Parse error response and throw with server detail message when available.
   */
  async _throwIfError(res, method, endpoint) {
    if (res.ok) return;
    let detail = `${method} ${endpoint} failed: ${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch {}
    throw new Error(detail);
  }

  // Wait for the initial session (INITIAL_SESSION) after page refresh
  async waitForSession(timeoutMs = 5000) {
    const { data: { session } } = await this.supabaseClient.auth.getSession();
    if (session) return session;

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        sub?.unsubscribe();
        reject(new Error('Timeout waiting for Supabase session'));
      }, timeoutMs);

      const { data: { subscription: sub } } =
        this.supabaseClient.auth.onAuthStateChange((_event, s) => {
          if (s) {
            clearTimeout(timer);
            sub.unsubscribe();
            resolve(s);
          }
        });
    });
  }

  // Ensure a fresh token by checking the session
  async ensureFreshToken() {
    const { data: { session } } = await this.supabaseClient.auth.getSession();
    if (session?.access_token) return session.access_token;

    const awaited = await this.waitForSession();
    return awaited.access_token;
  }

  // Make an authenticated request (GET, POST, PUT, DELETE, etc.)
  async authenticatedRequest(endpoint, options = {}, access_token = null) {
    const accessToken = access_token ? access_token : await this.ensureFreshToken();

    const isFormData = options.body instanceof FormData;
    const requestOptions = {
      ...options,
      headers: {
        ...(isFormData ? { Authorization: `Bearer ${accessToken}` }
          : {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          }),
        ...(options.headers || {}),
      },
    };

    const fullURL = `${this.baseURL}${endpoint}`;
    const response = await fetch(fullURL, requestOptions);

    if (response.status === 401) {
      const { data: { session: newSession }, error } =
        await this.supabaseClient.auth.refreshSession();

      if (error || !newSession) {
        throw new Error('Authentication failed - please log in again');
      }

      const retryOptions = {
        ...requestOptions,
        headers: {
          ...requestOptions.headers,
          Authorization: `Bearer ${newSession.access_token}`,
        },
      };

      return fetch(fullURL, retryOptions);
    }

    return response;
  }

  // GET request method
  async get(endpoint, options, accessToken = null) {
    const res = await this.authenticatedRequest(endpoint, { method: 'GET', ...options }, accessToken);

    await this._throwIfError(res, 'GET', endpoint);

    return res.status === 204 ? null : res.json();
  }

  // POST request method
  async post(endpoint, data, options) {
    const res = await this.authenticatedRequest(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });

    await this._throwIfError(res, 'POST', endpoint);

    return res.json();
  }

  // PUT request method
  async put(endpoint, data, options) {
    const res = await this.authenticatedRequest(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });

    await this._throwIfError(res, 'PUT', endpoint);

    return res.json();
  }

  // DELETE request method
  async delete(endpoint, options) {
    const res = await this.authenticatedRequest(endpoint, { method: 'DELETE', ...options });

    await this._throwIfError(res, 'DELETE', endpoint);

    return res.status === 204 ? null : res.json();
  }

  // PATCH request method
  async patch(endpoint, data, options) {
    const res = await this.authenticatedRequest(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });

    await this._throwIfError(res, 'PATCH', endpoint);

    return res.json();
  }
}

// Create an instance of the ApiClient
export const apiClient = new ApiClient();

// Export the ApiClient class itself
export { ApiClient };

